import { useCallback } from 'react';
import { getCountryCode, getLanguageCode } from 'typescript/utilities/locales';
import {
  PaymentSchedule,
  PaymentType,
} from 'typescript/constants/enums/payment';
import {
  OrderCompletionTelemetry,
  TOrderSummary,
} from 'typescript/types/api/solidshop';
import { isOrderFree, orderIsWarranty } from 'typescript/utilities/order_data';

type CheckoutVersion = 'v2' | 'v3';
type ErrorStage =
  | 'tokenize'
  | 'pre_auth_fraud'
  | 'create_payment'
  | 'complete_order';

interface UseLogCompleteOrderActionParams {
  checkoutVersion?: CheckoutVersion;
  orderNumber: string;
  orderSummaryData?: TOrderSummary;
}

interface LogCompleteOrderActionParams {
  status: 'initiated' | 'success' | 'error' | 'cancelled';
  paymentType?: PaymentType;
  paymentSchedule?: PaymentSchedule;
  errorStage?: ErrorStage;
  errorDetails?: string;
  // Completion returns final cart dimensions because the server may remove
  // stranded bundle lines between the pre-submit summary and acceptance.
  telemetry?: OrderCompletionTelemetry;
}

/**
 * Hook for logging complete order CTA actions to Datadog RUM.
 * Used across paid (payment page) and free (delivery page) order flows
 * to track CTA presses, successes, and errors under a single action name.
 *
 * @param checkoutVersion - 'v2' or 'v3' to identify which checkout version triggered the action
 * @param orderNumber - the order number to include in the logged action
 * @param orderSummaryData - order summary used to derive checkout and experiment dimensions
 * @returns logCompleteOrderAction - async function to log the action
 */
const useLogCompleteOrderAction = ({
  checkoutVersion = 'v3',
  orderNumber,
  orderSummaryData,
}: UseLogCompleteOrderActionParams) => {
  const logCompleteOrderAction = useCallback(
    async (params: LogCompleteOrderActionParams) => {
      const { datadogRum } = await import('@datadog/browser-rum');
      const { telemetry, ...actionParams } = params;
      const dimensions = {
        ...orderSummaryData,
        ...telemetry,
      };
      const storeCountry = dimensions.store_country?.toString().toUpperCase();

      datadogRum.addAction('complete-order-cta-action', {
        ...actionParams,
        checkoutVersion,
        orderType: orderIsWarranty(orderSummaryData) ? 'warranty' : 'normal',
        isFreeOrder: isOrderFree(orderSummaryData?.payment_due),
        deliveryMethod: orderSummaryData?.bopis_fulfillment_selected
          ? 'pickup_in_store'
          : 'ship_to_me',
        saasShopHardwareBundleExperimentArm:
          dimensions.saas_shop_hardware_bundle_experiment_arm ?? 'none',
        squareOneTier: dimensions.square_one_tier ?? 'none',
        subscriptionOrder: Boolean(dimensions.has_subscription),
        orderNumber,
        // Preserve the locale-derived `countryCode` for existing RUM queries;
        // `storeCountry` is the canonical store dimension shared with API and
        // metrics.
        countryCode: getCountryCode(),
        storeCountry: storeCountry || 'UNKNOWN',
        languageCode: getLanguageCode(),
      });
    },
    [checkoutVersion, orderSummaryData, orderNumber],
  );

  return { logCompleteOrderAction };
};

export { useLogCompleteOrderAction };
